(() => {
  // Purchase limit per variant (color + size), driven by product tags:
  //   no tag -> 3, max{N} (N = 1..50) -> N, nolimit -> unlimited.
  // Keep in sync with snippets/pitb-purchase-limit.liquid and
  // apps/pitb-functions/extensions/pitb-checkout-validation (productLimit).
  const DEFAULT_LIMIT = 3;

  const limitsByHandle = new Map(); // product handle -> number | Infinity
  const loadingLimits = new Map(); // product handle -> Promise (in-flight /products/<handle>.js)

  // max{N} with N = 1..MAX_TAG_NUMBER written as plain digits; keep in sync with the Liquid snippet,
  // the checkout function and the draft-order app.
  const MAX_TAG_NUMBER = 50;

  const limitFromTags = (tags) => {
    const keys = (Array.isArray(tags) ? tags : String(tags || '').split(','))
      .map((tag) => String(tag).trim().toLowerCase());
    if (keys.includes('nolimit')) return Infinity;
    const caps = keys
      .map((key) => /^max([1-9]\d*)$/.exec(key))
      .filter(Boolean)
      .map((match) => Number(match[1]))
      .filter((value) => value >= 1 && value <= MAX_TAG_NUMBER);
    return caps.length ? Math.min(...caps) : DEFAULT_LIMIT;
  };

  const parseLimit = (value) => {
    if (value === 'unlimited') return Infinity;
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : undefined;
  };

  // String form used in data-pitb-limit attributes ("unlimited" or an integer).
  const limitAttr = (tags) => {
    const limit = limitFromTags(tags);
    return limit === Infinity ? 'unlimited' : String(limit);
  };

  const isExcludedItem = (item) => {
    const fees = window.PITBEntryFee;
    const handle = item.handle || item.product_handle;
    return handle === 'pitb-sg-popup-entry-fee' || handle === 'self-collect-only' ||
      Boolean(fees && (fees.isFeeItem(item) || fees.isSelfCollectItem(item)));
  };

  // Limits rendered by Liquid (window.PITBPurchaseLimits) and by product forms
  // (data-pitb-handle / data-pitb-limit) are read synchronously.
  const readKnownLimits = () => {
    const seeded = window.PITBPurchaseLimits;
    if (seeded && typeof seeded === 'object') {
      for (const [handle, value] of Object.entries(seeded)) {
        const limit = parseLimit(value);
        if (limit !== undefined) limitsByHandle.set(handle, limit);
      }
    }
    document.querySelectorAll('product-form form[data-pitb-handle]').forEach((form) => {
      const data = form.dataset;
      if (!data || !data.pitbHandle) return;
      const limit = parseLimit(data.pitbLimit);
      if (limit !== undefined) limitsByHandle.set(data.pitbHandle, limit);
    });
  };

  const limitForHandle = (handle) => {
    readKnownLimits();
    const limit = handle ? limitsByHandle.get(handle) : undefined;
    return limit === undefined ? DEFAULT_LIMIT : limit;
  };

  const limitForItem = (item) => limitForHandle(item.handle || item.product_handle);

  // Cart lines carry no tags, so unknown products are looked up once via /products/<handle>.js.
  const ensureLimits = async (cart) => {
    readKnownLimits();
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const handles = new Set();
    for (const item of (cart && cart.items) || []) {
      const handle = item.handle || item.product_handle;
      if (handle && !isExcludedItem(item) && !limitsByHandle.has(handle)) handles.add(handle);
    }
    await Promise.all(Array.from(handles, (handle) => {
      if (!loadingLimits.has(handle)) {
        loadingLimits.set(handle, (async () => {
          try {
            const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
              credentials: 'same-origin', headers: { Accept: 'application/json' },
            });
            if (!response.ok) return;
            const product = await response.json();
            limitsByHandle.set(handle, limitFromTags(product && product.tags));
          } catch (_) {
            // Leave unknown: the default applies and the next cart read retries.
          } finally {
            loadingLimits.delete(handle);
          }
        })());
      }
      return loadingLimits.get(handle);
    }));
  };

  const errorsForCart = (cart) => {
    const totals = new Map();
    for (const item of (cart && cart.items) || []) {
      if (isExcludedItem(item)) continue;
      const id = String(item.variant_id || item.id);
      const total = totals.get(id) || {
        title: item.product_title || item.title, quantity: 0, limit: limitForItem(item),
      };
      total.quantity += Number(item.quantity) || 0;
      totals.set(id, total);
    }
    return Array.from(totals.values())
      .filter((item) => item.quantity > item.limit)
      .map((item) => `${item.title} has a limit of ${item.limit} per order. Please reduce the quantity to continue.`);
  };

  const setHidden = (element, hidden) => {
    if (!element) return;
    element.hidden = hidden;
    if (element.style) element.style.display = hidden ? 'none' : '';
  };

  const syncControls = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return;
    syncAddForms(cart);
    document.querySelectorAll('input[data-quantity-variant-id]').forEach((input) => {
      const line = cart.items[Number(input.dataset.index) - 1];
      if (!line) return;
      if (isExcludedItem(line)) return;
      const limit = limitForItem(line);
      const total = cart.items.filter((item) => String(item.variant_id || item.id) === String(line.variant_id || line.id))
        .reduce((sum, item) => sum + Number(item.quantity), 0);
      const remaining = Math.max(0, limit - (total - Number(line.quantity)));
      if (input.dataset.originalMax === undefined) input.dataset.originalMax = input.max || '';
      const originalMax = input.dataset.originalMax;
      const max = originalMax === '' ? remaining : Math.min(remaining, Number(originalMax));
      input.max = Number.isFinite(max) ? String(max) : '';
      // cart.js clamps typed values to this attribute, so it must be absent (not '') when uncapped.
      if (Number.isFinite(max)) input.dataset.quantityLimitMax = String(max);
      else delete input.dataset.quantityLimitMax;
      const wrapper = input.closest('quantity-input');
      // max1: no quantity selector in the cart (the remove link stays).
      setHidden(wrapper, limit === 1);
      const cell = input.closest('.cart-item__quantity');
      if (cell && cell.classList) cell.classList.toggle('cart-item__quantity--no-qty', limit === 1);
      const button = wrapper?.querySelector('button[name="plus"]');
      if (button) {
        const disabled = Number(input.value) >= max || input.disabled;
        button.disabled = disabled;
        button.classList.toggle('disabled', disabled);
        button.setAttribute('aria-disabled', String(disabled));
      }
    });
  };

  let latestCart = null;
  let addQueue = Promise.resolve();

  // Product forms on the page know their product (handle) and, via the hidden id input, their variant.
  const handleForVariant = (cart, variantId) => {
    const line = (cart.items || []).find((item) => String(item.variant_id || item.id) === String(variantId));
    if (line) return line.handle || line.product_handle;
    for (const form of document.querySelectorAll('product-form form[data-pitb-handle]')) {
      if (form.dataset && form.dataset.pitbHandle &&
          String(form.querySelector('[name="id"]')?.value) === String(variantId)) {
        return form.dataset.pitbHandle;
      }
    }
    return undefined;
  };

  const remainingForVariant = (cart, variantId) => {
    const lines = (cart.items || []).filter((item) => String(item.variant_id || item.id) === String(variantId));
    if (lines.some(isExcludedItem)) return Infinity;
    const limit = limitForHandle(handleForVariant(cart, variantId));
    if (limit === Infinity) return Infinity;
    return Math.max(0, limit - lines.reduce((total, item) => total + Number(item.quantity), 0));
  };

  const syncAddForms = (cart = latestCart) => {
    if (!cart) return;
    latestCart = cart;
    document.querySelectorAll('product-form form').forEach((form) => {
      const id = form.querySelector('[name="id"]')?.value;
      const button = form.querySelector('[type="submit"]');
      if (!id || !button) return;
      const remaining = remainingForVariant(cart, id);
      const input = form.querySelector('[name="quantity"]');
      // Limited products: "Limit N per customer" until the cart holds the limit, then the red notice.
      const note = form.querySelector('[data-pitb-limit-note]');
      const reached = form.querySelector('[data-pitb-limit-reached]');
      if (note && reached && Number.isFinite(remaining)) {
        note.hidden = remaining === 0;
        reached.hidden = remaining > 0;
      }
      if (remaining === 0 && !button.disabled) {
        button.dataset.quantityLimitDisabled = 'true';
        button.disabled = true;
      } else if (remaining > 0 && button.dataset.quantityLimitDisabled === 'true') {
        delete button.dataset.quantityLimitDisabled;
        button.disabled = false;
      }
      if (!input || input.type === 'hidden') return;
      if (input.dataset.addOriginalMax === undefined) input.dataset.addOriginalMax = input.max || '';
      const original = input.dataset.addOriginalMax;
      const plus = form.querySelector('[data-pitb-qty-plus], button[name="plus"]');
      if (!Number.isFinite(remaining)) {
        // nolimit (or a ticket line): uncapped selector, back to whatever the markup allowed.
        input.max = original;
        if (plus) plus.disabled = original !== '' && Number(input.value) >= Number(original);
        return;
      }
      const max = Math.max(1, original === '' ? remaining : Math.min(Number(original), remaining));
      input.max = String(max);
      input.value = String(Math.min(Number(input.value) || 1, max));
      if (plus) plus.disabled = remaining === 0 || Number(input.value) >= max;
    });
  };

  const readCart = async () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Could not load cart');
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
    await ensureLimits(cart);
    syncAddForms(cart);
    return cart;
  };

  const refresh = () => readCart().catch(() => null);

  const addToCart = (url, config) => {
    // Serialize adds from different product forms so each reads the previous result.
    const run = async () => {
      const cart = await readCart();
      const data = config.body;
      const remaining = remainingForVariant(cart, data.get('id'));
      if (remaining === 0) return null;
      data.set('quantity', String(Math.min(Math.max(1, Number(data.get('quantity')) || 1), remaining)));
      const response = await fetch(url, config);
      if (response.ok) await refresh();
      return response;
    };
    const pending = addQueue.then(run, run);
    addQueue = pending.catch(() => {});
    return pending;
  };

  // Back/forward navigation can restore a stale PDP from the browser cache.
  window.addEventListener('pageshow', refresh);
  window.addEventListener('focus', refresh);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();

  const validateCheckout = async () => {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Could not load cart');
      const cart = await response.json();
      await ensureLimits(cart);
      const errors = errorsForCart(cart);
      if (window.PITBBooking) {
        window.PITBBooking.noteCart(cart);
        window.PITBBooking.syncCheckoutGate();
      }
      syncControls(cart);
      if (errors.length) return false;
      return true;
    } catch (_) {
      window.alert('We could not check your cart. Please try again.');
      return false;
    }
  };

  window.PITBQuantityLimit = {
    errorsForCart, syncControls, validateCheckout, remainingForVariant, syncAddForms, refresh, addToCart,
    limitFromTags, limitAttr, limitForItem, ensureLimits,
  };
})();
